import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

interface ConfirmDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 72px;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 211;
  padding: 0 16px;

  @media (max-width: 768px) {
    top: 0;
  }
`;

const DialogBox = styled.div`
  background: #ffffff;
  padding: 32px 24px;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const Title = styled.h2`
  font-family: 'GilroyRegular';
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #111;
  text-align: center; 
`;

const Message = styled.p`
  font-family: 'GilroyRegular';
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: #444;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: stretch;

  & > button {
    flex: 1;
  }

  @media (min-width: 400px) {
    flex-direction: row;
  }
`;

const BaseButton = styled.button`
  flex: 1;
  padding: 14px;
  font-size: 16px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, transform 0.1s ease;
  user-select: none;

  &:active {
    transform: scale(0.97);
  }
`;

const CancelButton = styled(BaseButton)`
  background-color: #f9f9f9;
  font-family: 'GilroyRegular';
  color: #333;
  border: 1px solid #ccc;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const SuccessMessage = styled.div`
  position: fixed;
  font-family: 'GilroyRegular';
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #2c2c2c; 
  color: #ffffff;
  padding: 14px 20px;
  border-radius: 12px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 320px;
  max-width: 90%;
  animation: fadeInOut 3.8s ease forwards;

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    10% {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    90% {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
  }
`;



const ConfirmButton = styled(BaseButton)`
  background-color: #c62828 !important;
  color: #ffffff !important;
  border: none;
  font-family: 'GilroyRegular';

  &:hover {
    background-color: #8e0000 !important;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.3);
  }
`;

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title = 'Are you sure?',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Delete',
  cancelText = 'Cancel',
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && cancelRef.current) {
      cancelRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="dialog-title">
      <DialogBox>
        <Title id="dialog-title">{title}</Title>
        <Message>{message}</Message>
        <ButtonGroup>
          <CancelButton ref={cancelRef} onClick={onCancel}>
            {cancelText}
          </CancelButton>
          <ConfirmButton onClick={onConfirm}>{confirmText}</ConfirmButton>
        </ButtonGroup>
      </DialogBox>
    </Overlay>
  );
};

export default ConfirmDialog;
export { SuccessMessage };

